import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StoryElement } from '../../Domain/StoryElement';
import { resetAppState } from '../actions/resetAppState';

export enum ChangeType {
    Added = 'Added',
    Removed = 'Removed',
    Modified = 'Modified'
}

interface StoryElementChange {
    element: StoryElement;
    changeType: ChangeType;
    timestamp: number;
}

interface StoryElementState {
    elements: StoryElement[];
    changes: StoryElementChange[];
    isLoading: boolean;
    error: string | null;
}

const createInitialState = (): StoryElementState => ({
    elements: [],
    changes: [],
    isLoading: false,
    error: null,
});

const initialState: StoryElementState = createInitialState();

export const storyElementSlice = createSlice({
    name: 'storyElement',
    initialState,
    reducers: {
        addElement: (state, action: PayloadAction<StoryElement>) => {
            state.elements.push(action.payload);
            state.changes.push({
                element: action.payload,
                changeType: ChangeType.Added,
                timestamp: Date.now(),
            });
        },
        removeElement: (state, action: PayloadAction<string>) => {
            const element = state.elements.find(e => e.id === action.payload);
            if (element) {
                state.elements = state.elements.filter(e => e.id !== action.payload);
                state.changes.push({
                    element,
                    changeType: ChangeType.Removed,
                    timestamp: Date.now(),
                });
            }
        },
        updateElement: (state, action: PayloadAction<StoryElement>) => {
            const index = state.elements.findIndex(e => e.id === action.payload.id);
            if (index !== -1) {
                state.elements[index] = action.payload;
                state.changes.push({
                    element: action.payload,
                    changeType: ChangeType.Modified,
                    timestamp: Date.now(),
                });
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetAppState, () => createInitialState());
    }
});

export const {
    addElement,
    removeElement,
    updateElement,
    setLoading,
    setError,
} = storyElementSlice.actions;

export default storyElementSlice.reducer; 